import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { FinancialAccountType } from '../entities/treasury.model';
import type {
  CashFlowEntry,
  CashSession,
  FinancialAccount,
} from '../entities/treasury.model';
import { TreasuryService } from './treasury.service';

/** Raw API bundle returned by `loadTurnContext`. */
export interface TreasuryCashTurnLoadRaw {
  account: FinancialAccount | null;
  session: CashSession | null;
  entries: CashFlowEntry[];
  /** True when the route pointed at a session that no longer exists. */
  sessionLookupFailed: boolean;
}

@Injectable({ providedIn: 'root' })
export class TreasuryCashTurnContextService {
  private readonly treasuryService = inject(TreasuryService);

  public loadTurnContext(params: {
    financialAccountId: string;
    cashSessionId: string | null;
  }): Observable<TreasuryCashTurnLoadRaw> {
    const id = params.financialAccountId;
    const cashSessionIdParam = params.cashSessionId?.trim() ?? null;

    if (cashSessionIdParam) {
      return this.loadTurnContextBySessionId(id, cashSessionIdParam);
    }

    return this.resolveAccountForOpenTurn$(id);
  }

  private loadTurnContextBySessionId(
    financialAccountId: string,
    cashSessionId: string,
  ): Observable<TreasuryCashTurnLoadRaw> {
    return this.treasuryService.getCashSession(cashSessionId).pipe(
      switchMap((session) => {
        if (
          !session ||
          session.FinancialAccountId !== financialAccountId
        ) {
          return this.resolveAccountForOpenTurn$(
            financialAccountId,
            { sessionLookupFailed: true },
          );
        }

        return this.loadTurnContextFromSession$(session);
      }),
    );
  }

  private loadTurnContextFromSession$(
    session: CashSession,
  ): Observable<TreasuryCashTurnLoadRaw> {
    // Main reworked this whole function: instead of fetching the account
    // and scanning its sessions, the caller now always arrives with a
    // resolved `session` (via `loadTurnContextBySessionId`) and we just
    // synthesize the account from session fields + load entries.
    //
    // Our slot-refactor change in this same function (swapping
    // `getCashSessions` → `getAccountCashSessions`) is obsolete here — that
    // code path no longer exists. `getAccountCashSessions` itself stays on
    // the service because the per-account page (`loadAccountCashSessions`
    // rxMethod) still needs it.
    return this.loadSessionEntries$(session).pipe(
      map((entries) => ({
        account: this.buildSessionAccount(session),
        session,
        entries,
        sessionLookupFailed: false,
      })),
    );
  }

  /**
   * Account-only route: load account metadata for the NO_SESSION open-turn flow.
   * Existing turns must be addressed by `cashSessionId` in the URL.
   */
  private resolveAccountForOpenTurn$(
    financialAccountId: string,
    options?: { sessionLookupFailed?: boolean },
  ): Observable<TreasuryCashTurnLoadRaw> {
    return this.treasuryService.getFinancialAccount(financialAccountId).pipe(
      map((account) => {
        if (!account) {
          return {
            account: null,
            session: null,
            entries: [],
            sessionLookupFailed: false,
          };
        }

        if (
          !account.Active ||
          account.Type !== FinancialAccountType.Cash
        ) {
          return {
            account,
            session: null,
            entries: [],
            sessionLookupFailed: false,
          };
        }

        return {
          account,
          session: null,
          entries: [],
          sessionLookupFailed: options?.sessionLookupFailed ?? false,
        };
      }),
    );
  }

  private buildSessionAccount(session: CashSession): FinancialAccount {
    return {
      FinancialAccountId: session.FinancialAccountId,
      Name: session.FinancialAccountName?.trim() || 'Conta sem nome',
      Type: FinancialAccountType.Cash,
      CurrentBalance:
        session.ExpectedBalance ??
        session.ClosingBalance ??
        session.OpeningBalance,
      InitialBalance: session.OpeningBalance,
      AllowNegative: false,
      Active: true,
      IsSimpleCashSession: session.IsSimpleCashSession,
    };
  }

  private loadSessionEntries$(session: CashSession | null): Observable<CashFlowEntry[]> {
    if (!session) {
      return of([]);
    }
    return this.treasuryService.getSessionCashFlowEntries(session.CashSessionId);
  }
}
